/* ==========================================================================
   Builder gel story: the self-leveling demonstration.

   A cross-section of a nail under a layer of builder gel, drawn in one SVG
   (sections/gel-builder-landing.liquid). The surface starts as the brush
   leaves it, ridged and lumpy, and relaxes to a smooth apex while a clock
   runs from 0 to 60 seconds. Each ridge fades on its own clock, the
   finest first and the long lumps last, as a thin film levels: the
   capillary pull that flattens a ripple grows steeply as the ripple
   shortens.

   The reflection is computed rather than drawn: a strip of light shows
   wherever the surface tilts towards the viewer, so on ridges it breaks
   into glints and on the levelled surface it runs as one clean line,
   which is what "glass" looks like.

   The page markup holds the final frame (and the brush-mark ghost), so
   with JS off or under reduced motion the card shows the levelled gel.
   This script replays it once, when the card is well into view, and adds
   the scrubber and the replay button. Dragging the scrubber stops the
   replay and shows any second of the minute.

   Shares nothing with assets/nail-drill.js beyond the page; re-inits on
   shopify:section:load for the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  var SELECTOR = '[data-gb-level]';
  var SECONDS = 60;
  var HOLD_MS = 900; // the brush-mark frame, held so the eye catches it
  var RUN_MS = 6200; // the minute, played in this many milliseconds
  var ACTIVE_START = 8; // the first line reads as current until this second
  var ACTIVE_END = 52; // the second line reads as current from this second

  /* ------------------------------------------------------------------------
     Geometry, in the SVG's 1000 by 520 user space
     ------------------------------------------------------------------------ */

  var X0 = 112; // the gel starts just past the cuticle
  var X1 = 948; // and stops short of the free edge
  var APEX_U = 0.42; // the apex sits in the stress area, nearer the cuticle
  var APEX_H = 108;
  var SAMPLES = 220;
  var APEX_P = Math.log(0.5) / Math.log(APEX_U);

  /* The ridges a brush leaves: long lumps where the bead was dragged, the
     strokes, the bristle lines and the finest texture. Amplitude in user
     units, frequency across the gel, phase, and the time constant in
     seconds. */
  var RIDGES = [
    { a: 8, f: 2.6, phase: 0.7, tau: 21 },
    { a: 7, f: 6.8, phase: 2.2, tau: 11 },
    { a: 4, f: 15.5, phase: 4.1, tau: 5.5 },
    { a: 1.8, f: 31, phase: 1.3, tau: 2.4 }
  ];

  /* Reflection: the window of slopes that send the light to the viewer. */
  var GLINT_SLOPE = 0.02;
  var GLINT_WIDTH = 0.115;
  var GLINT_DROP = 6;

  function nailTop(x) {
    var d = (x - 520) / 470;
    return 336 + 16 * d * d;
  }

  function surface(t) {
    var points = [];
    var decay = RIDGES.map(function (r) {
      return r.a * Math.exp(-t / r.tau);
    });

    for (var i = 0; i <= SAMPLES; i++) {
      var u = i / SAMPLES;
      var x = X0 + (X1 - X0) * u;
      var base = APEX_H * Math.pow(Math.sin(Math.PI * Math.pow(u, APEX_P)), 0.82);
      var envelope = Math.pow(Math.sin(Math.PI * u), 0.75);
      var ridge = 0;

      for (var k = 0; k < RIDGES.length; k++) {
        ridge += decay[k] * Math.sin(2 * Math.PI * RIDGES[k].f * u + RIDGES[k].phase);
      }

      var thickness = Math.max(base + envelope * ridge, 0.6);
      points.push([x, nailTop(x) - thickness]);
    }

    return points;
  }

  function fmt(n) {
    return Math.round(n * 10) / 10;
  }

  function linePath(points, dy) {
    var d = '';
    for (var i = 0; i < points.length; i++) {
      d += (i ? 'L' : 'M') + fmt(points[i][0]) + ' ' + fmt(points[i][1] + (dy || 0));
    }
    return d;
  }

  /* The gel body: along the surface, then back along the nail. */
  function bodyPath(points) {
    var d = linePath(points);
    for (var x = X1; x >= X0; x -= 19) {
      d += 'L' + fmt(x) + ' ' + fmt(nailTop(x));
    }
    return d + 'L' + fmt(X0) + ' ' + fmt(nailTop(X0)) + 'Z';
  }

  /* The runs of surface whose slope falls inside the reflection window. */
  function glintPath(points) {
    var d = '';
    var open = false;

    for (var i = 1; i < points.length - 1; i++) {
      var slope = (points[i + 1][1] - points[i - 1][1]) / (points[i + 1][0] - points[i - 1][0]);
      var lit = Math.abs(slope - GLINT_SLOPE) < GLINT_WIDTH;

      if (lit) {
        d += (open ? 'L' : 'M') + fmt(points[i][0]) + ' ' + fmt(points[i][1] + GLINT_DROP);
        open = true;
      } else {
        open = false;
      }
    }

    return d || 'M0 0';
  }

  /* ------------------------------------------------------------------------
     One card
     ------------------------------------------------------------------------ */

  function Level(card) {
    this.card = card;
    this.body = card.querySelector('[data-gb-body]');
    this.edge = card.querySelector('[data-gb-edge]');
    this.glints = card.querySelectorAll('[data-gb-glint]');
    this.ghost = card.querySelector('[data-gb-ghost]');
    this.clock = card.querySelector('[data-gb-clock]');
    this.range = card.querySelector('[data-gb-range]');
    this.replay = card.querySelector('[data-gb-replay]');
    this.lineStart = card.querySelector('[data-gb-line="start"]');
    this.lineEnd = card.querySelector('[data-gb-line="end"]');
    this.unit = this.range ? this.range.getAttribute('data-gb-unit') || '' : '';
    this.frame = null;
    this.t = SECONDS;
    this.ready = Boolean(this.body && this.edge);

    if (!this.ready) return;

    if (this.ghost) this.ghost.setAttribute('d', linePath(surface(0)));

    var self = this;

    if (this.range) {
      this.range.hidden = false;
      this.range.addEventListener('input', function () {
        self.stop();
        self.render(parseFloat(self.range.value) || 0);
      });
    }

    if (this.replay) {
      this.replay.hidden = false;
      this.replay.addEventListener('click', function () {
        self.play();
      });
    }

    card.classList.add('is-live');
    this.render(SECONDS);
  }

  Level.prototype.render = function (t) {
    this.t = t;

    var points = surface(t);
    var glint = glintPath(points);

    this.body.setAttribute('d', bodyPath(points));
    this.edge.setAttribute('d', linePath(points));
    for (var i = 0; i < this.glints.length; i++) this.glints[i].setAttribute('d', glint);

    var second = Math.round(t);
    if (this.clock) this.clock.textContent = second;

    if (this.range) {
      this.range.value = String(second);
      this.range.setAttribute('aria-valuetext', second + ' ' + this.unit);
      this.range.style.setProperty('--gb-progress', (t / SECONDS) * 100 + '%');
    }

    if (this.lineStart) this.lineStart.classList.toggle('is-active', t < ACTIVE_START);
    if (this.lineEnd) this.lineEnd.classList.toggle('is-active', t >= ACTIVE_END);
  };

  Level.prototype.stop = function () {
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = null;
    this.card.classList.remove('is-playing');
  };

  Level.prototype.play = function () {
    var self = this;
    var start = null;

    this.stop();
    this.card.classList.add('is-playing');
    this.render(0);

    function tick(now) {
      if (start === null) start = now;

      var elapsed = now - start - HOLD_MS;
      var p = Math.min(Math.max(elapsed / RUN_MS, 0), 1);

      self.render(p * SECONDS);

      if (p < 1) {
        self.frame = window.requestAnimationFrame(tick);
      } else {
        self.frame = null;
        self.card.classList.remove('is-playing');
      }
    }

    this.frame = window.requestAnimationFrame(tick);
  };

  /* ------------------------------------------------------------------------
     Init
     ------------------------------------------------------------------------ */

  function init(scope) {
    var cards = (scope || document).querySelectorAll(SELECTOR);
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    Array.prototype.forEach.call(cards, function (card) {
      if (card.gbLevel) return;

      var level = new Level(card);
      card.gbLevel = level;

      if (!level.ready || reduced || !('IntersectionObserver' in window)) return;

      // The brush-mark frame waits, offstage, for the visitor.
      level.render(0);

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            if (level.t === 0) level.play();
          });
        },
        { threshold: 0.55 }
      );

      observer.observe(card.querySelector('.gb-level__stage') || card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
